/**
 * Staff Settings Page
 * Allows staff (Admin/Manager/Streamer) to change password with auto-generation
 * Staff-only page - viewers should use /settings instead
 */

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';
import { ROUTES } from '../lib/constants';

// Generate random password (16 chars)
const generatePassword = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 16; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
};

export default function StaffSettings() {
    const { t } = useTranslation('common');
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Check if user must change password (from AdminLogin redirect)
    const mustChangePassword = location.state?.mustChangePassword === true;

    // Determine back navigation based on role
    const isStreamer = user?.role === 'STREAMER';
    const backRoute = isStreamer ? ROUTES.STUDIO : ROUTES.CONSOLE;
    const backLabel = isStreamer
        ? t('navigation.backToStudio', 'スタジオに戻る')
        : t('navigation.backToConsole', '管理画面に戻る');

    // Password change state
    const [currentPassword, setCurrentPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
    const [passwordCopied, setPasswordCopied] = useState(false);

    // Password visibility state
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordError(null);
        setGeneratedPassword(null);
        setPasswordCopied(false);

        if (!currentPassword) {
            setPasswordError('現在のパスワードを入力してください');
            return;
        }

        setPasswordLoading(true);
        try {
            // Generate new password
            const newPassword = generatePassword();

            // Staff users use /api/users/me/password
            await apiClient.changePassword({
                old_password: currentPassword,
                new_password: newPassword,
            });

            // Show the generated password for copying
            setGeneratedPassword(newPassword);
            setCurrentPassword('');
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : t('settings.password.error');
            setPasswordError(errorMessage);
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleCopyPassword = async () => {
        if (!generatedPassword) return;
        try {
            await navigator.clipboard.writeText(generatedPassword);
            setPasswordCopied(true);
        } catch (err) {
            console.error('Failed to copy password:', err);
        }
    };

    const handleContinue = () => {
        // Navigate to appropriate page after password change
        if (user?.role === 'ADMIN' || user?.role === 'MANAGER') {
            navigate(ROUTES.CONSOLE);
        } else if (user?.role === 'STREAMER') {
            navigate(ROUTES.STUDIO);
        }
    };

    const handleLogout = () => {
        logout();
        navigate(ROUTES.STAFF_LOGIN);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Bar */}
            <header className="bg-accent text-white shadow-md">
                <div className="container mx-auto px-4 py-3">
                    <div className="flex items-center justify-between">
                        <h1 className="text-2xl font-bold flex items-center">
                            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {t('settings.title')}
                        </h1>
                        <div className="flex items-center gap-4">
                            <div className="text-sm">
                                <span className="opacity-90">{t('header.loggedInAs')}:</span>{' '}
                                <span className="font-semibold">{user?.username}</span>
                            </div>
                            <div className="flex gap-2">
                                {!mustChangePassword && !generatedPassword && (
                                    <Link
                                        to={backRoute}
                                        className="bg-accent-50 text-accent-700 hover:bg-accent-100 text-sm font-semibold py-2 px-4 rounded transition-colors shadow-sm"
                                    >
                                        {backLabel}
                                    </Link>
                                )}
                                <button
                                    onClick={handleLogout}
                                    className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2 px-4 rounded transition-colors"
                                >
                                    {t('buttons.logout')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-2xl mx-auto space-y-8">
                    {/* Must Change Password Warning */}
                    {mustChangePassword && !generatedPassword && (
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-yellow-700 font-medium">
                                        {t('settings.password.mustChange', 'セキュリティのため、初回ログイン時にパスワードを変更してください。')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Generated Password Display */}
                    {generatedPassword && (
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4">
                                <p className="text-sm text-yellow-800 font-medium mb-3">
                                    ⚠️ 新しいパスワードが生成されました。必ずコピーして安全な場所に保存してください：
                                </p>
                                <div className="flex items-center gap-2">
                                    <code className="bg-white px-3 py-2 border border-yellow-300 font-mono flex-1 text-lg">
                                        {generatedPassword}
                                    </code>
                                    <button
                                        onClick={handleCopyPassword}
                                        className={`px-4 py-2 font-semibold rounded transition-colors ${
                                            passwordCopied
                                                ? 'bg-green-500 text-white'
                                                : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                                        }`}
                                    >
                                        {passwordCopied ? '✓ コピー済み' : 'コピー'}
                                    </button>
                                </div>
                            </div>
                            {passwordCopied && (
                                <button
                                    onClick={handleContinue}
                                    className="w-full bg-pink-500 text-white py-3 px-4 rounded-md hover:bg-pink-600 transition-colors font-semibold"
                                >
                                    {isStreamer ? 'スタジオへ進む →' : '管理画面へ進む →'}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Password Change Form */}
                    {!generatedPassword && (
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <svg className="w-5 h-5 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                {t('settings.password.title')}
                            </h2>
                            <form onSubmit={handlePasswordChange} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('settings.password.current')}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showCurrentPassword ? 'text' : 'password'}
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                            aria-label={showCurrentPassword ? t('password.hide') : t('password.show')}
                                        >
                                            {showCurrentPassword ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600">
                                    新しいパスワードは自動生成されます（16文字）
                                </p>
                                {passwordError && (
                                    <div className="text-red-500 text-sm">{passwordError}</div>
                                )}
                                <button
                                    type="submit"
                                    disabled={passwordLoading}
                                    className="w-full bg-pink-500 text-white py-2 px-4 rounded-md hover:bg-pink-600 transition-colors disabled:opacity-50"
                                >
                                    {passwordLoading ? t('buttons.saving') : 'パスワードを再生成'}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Back navigation */}
                    {!mustChangePassword && !generatedPassword && (
                        <div className="text-center">
                            <Link
                                to={backRoute}
                                className="text-pink-500 hover:text-pink-600 font-medium"
                            >
                                {backLabel}
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
